<template>
  <div v-if="session.isAuthenticated" ref="bellRoot" class="notifications-bell">
    <button
      type="button"
      class="icon-button notifications-bell-button"
      :class="{ 'has-unread': notifications.hasUnread }"
      :title="notifications.hasUnread ? `${notifications.unreadCount} unread comment${notifications.unreadCount === 1 ? '' : 's'}` : 'No unread comments'"
      @click="toggleOpen"
    >
      <Bell class="icon" />
      <span v-if="notifications.hasUnread" class="notifications-badge">{{ badgeCount }}</span>
    </button>
    <div v-if="open" class="notifications-popover">
      <header class="notifications-header">
        <span class="notifications-title">Comments</span>
        <button
          v-if="notifications.hasUnread"
          type="button"
          class="notifications-mark-all"
          @click="markAllRead"
        >
          Mark all read
        </button>
      </header>
      <div v-if="notifications.loading && notifications.unreadComments.length === 0" class="notifications-empty">
        Loading…
      </div>
      <div v-else-if="!notifications.hasUnread" class="notifications-empty">No unread comments</div>
      <button
        v-for="comment in notifications.unreadComments"
        :key="comment.id"
        type="button"
        class="notifications-item"
        @click="openComment(comment)"
      >
        <span class="notifications-item-author">{{ comment.userName }}</span>
        <span class="notifications-item-snippet">{{ comment.content }}</span>
        <span class="notifications-item-meta">
          <span class="notifications-item-doc">{{ comment.documentTitle }}</span>
          <span class="notifications-item-time">{{ relativeTime(comment.createdAt) }}</span>
        </span>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Bell } from 'lucide-vue-next';
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import type { UnreadCommentResponse } from '../api/generated';
import { useSessionStore } from '../stores/session';
import { useNotificationsStore } from '../stores/notifications';

const session = useSessionStore();
const notifications = useNotificationsStore();
const router = useRouter();
const bellRoot = ref<HTMLElement | null>(null);
const open = ref(false);

const badgeCount = computed(() => (notifications.unreadCount > 99 ? '99+' : String(notifications.unreadCount)));

function toggleOpen(): void {
  open.value = !open.value;
}

async function openComment(comment: UnreadCommentResponse): Promise<void> {
  open.value = false;
  await notifications.markDocumentRead(String(comment.documentId));
  await router.push({ name: 'workspace-document', params: { documentId: String(comment.documentId) } });
}

async function markAllRead(): Promise<void> {
  const documentIds = new Set(notifications.unreadComments.map(comment => String(comment.documentId)));
  for (const documentId of documentIds) {
    await notifications.markDocumentRead(documentId);
  }
}

function closeIfOutside(event: PointerEvent): void {
  const element = bellRoot.value;
  if (!open.value || !(event.target instanceof Node) || element?.contains(event.target)) {
    return;
  }

  open.value = false;
}

function relativeTime(instant: unknown): string {
  if (typeof instant !== 'string' && typeof instant !== 'number') {
    return '';
  }

  const date = new Date(instant);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const seconds = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000));
  if (seconds < 45) {
    return 'just now';
  }

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    return `${minutes}m ago`;
  }

  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `${hours}h ago`;
  }

  const days = Math.floor(hours / 24);
  if (days < 7) {
    return `${days}d ago`;
  }

  return date.toLocaleDateString();
}

onMounted(() => {
  document.addEventListener('pointerdown', closeIfOutside);
});
onBeforeUnmount(() => {
  document.removeEventListener('pointerdown', closeIfOutside);
});
</script>
